
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaClipboardList, FaChartBar, FaCheckCircle } from 'react-icons/fa';
import './Dashboard.css';
import { fetchDashboardData } from '../../../api/AdminDashboardApi';
import { fetchAllExamResults } from '../../../api/quizResultApi';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalExams: 0,
    assignedExams: 0,
    completedExams: 0
  });
  const [recentExamResults, setRecentExamResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Admin Dashboard - Finhaat Gurukul';
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardStats, examResults] = await Promise.all([
        fetchDashboardData(),
        fetchAllExamResults()
      ]);
      
      // Sort exam results by userExamId in descending order
      const sortedResults = examResults.sort((a, b) => b.userExamId - a.userExamId);
      
      setDashboardData(dashboardStats);
      setRecentExamResults(sortedResults.slice(0, 5)); // Take first 5 from sorted results
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewMore = () => {
    navigate('/reports');
  };

  if (loading) {
    return <div className="spinner-container">
      <div className="spinner"></div>
    </div>
  }

  if (error) {
    return (
      <div className="spinner-container">
        <div className="no-data-admin-dashboard">Oops! Finhaat Gurukul hit a snag. Data will be back soon!</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-md flex items-center space-x-4">
          <FaUser className="text-blue-500 text-4xl" />
          <div>
            <h2 className="text-xl font-bold">{dashboardData.totalUsers}</h2>
            <p>Total Users</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md flex items-center space-x-4">
          <FaClipboardList className="text-green-500 text-4xl" />
          <div>
            <h2 className="text-xl font-bold">{dashboardData.totalExams}</h2>
            <p>Total Exams</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md flex items-center space-x-4">
          <FaChartBar className="text-yellow-500 text-4xl" />
          <div>
            <h2 className="text-xl font-bold">{dashboardData.assignedExams}</h2>
            <p>Assigned Exams</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md flex items-center space-x-4">
          <FaCheckCircle className="text-red-500 text-4xl" />
          <div>
            <h2 className="text-xl font-bold">{dashboardData.completedExams}</h2>
            <p>Completed Exams</p>
          </div>
        </div>
      </div>

      <div className="recent-exam-results">
        <div className="results-header">
          <h3>Recent Exam Results</h3>
          <button onClick={handleViewMore} className="view-more-btn-dashboard">View More</button>
        </div>
        <table className="results-table">
          <thead className='results-table-thead'>
            <tr>
              <th>User</th>
              <th>Exam</th>
              <th className="center-align">Total Questions</th>
              <th className="center-align">Attempted Questions</th>
              <th className="center-align">Correct Answers</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {recentExamResults.map((result, index) => (
              <tr key={result.userExamId}>
                <td>{result.userName}</td>
                <td>{result.examName}</td>
                <td className="center-align">{result.totalQuestions}</td>
                <td className="center-align">{result.attemptedQuestions}</td>
                <td className="center-align">{result.correctAnswers}</td>
                <td className={result.score > 0 ? "status-passed" : "status-failed"}>
                  {result.score > 0 ? "Passed" : "Failed"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;